import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import useChannelForm from '../hooks/useChannelForm';
import InstructionHistoryButton from '../../InstructionHistoryButton';
import { useModelCatalog, formatPrice } from '../../../../../config/modelCatalog';
import useIsMobile from '../../../useIsMobile';
import '../../AssistantSettingsTheme.css';

const REALTIME_VOICES = [
  { value: 'alloy',   label: 'Alloy'   },
  { value: 'ash',     label: 'Ash'     },
  { value: 'ballad',  label: 'Ballad'  },
  { value: 'coral',   label: 'Coral'   },
  { value: 'echo',    label: 'Echo'    },
  { value: 'sage',    label: 'Sage'    },
  { value: 'shimmer', label: 'Shimmer' },
  { value: 'verse',   label: 'Verse'   },
  { value: 'marin',   label: 'Marin'   },
  { value: 'cedar',   label: 'Cedar'   },
];

const TRANSCRIPTION_MODELS = [
  { value: 'whisper-1',             label: 'Whisper 1'             },
  { value: 'gpt-4o-mini-transcribe', label: 'GPT-4o Mini Transcribe' },
  { value: 'gpt-4o-transcribe',     label: 'GPT-4o Transcribe'     },
];

const TURN_DETECTION_TYPES = [
  { value: '',             label: 'None (disabled)' },
  { value: 'server_vad',   label: 'Server VAD'      },
  { value: 'semantic_vad', label: 'Semantic VAD'    },
];

const RealtimeChannelSettings = ({
  initialSettings = {},
  agentId = null,
  channelType = 'REALTIME',
  onSave,
  saving,
  error,
  success,
}) => {
  const { models: realtimeModels } = useModelCatalog('realtime');
  const isMobile = useIsMobile();

  const defaults = useMemo(
    () => ({
      model:                     initialSettings.model || 'gpt-realtime',
      voice:                     initialSettings.voice                     ?? 'alloy',
      input_audio_transcription: initialSettings.input_audio_transcription ?? { model: 'whisper-1' },
      turn_detection:            initialSettings.turn_detection            ?? { type: 'server_vad' },
      parallel_tool_calls:       initialSettings.parallel_tool_calls       ?? true,
      instructions:              initialSettings.instructions              ?? '',
      placeholder:               initialSettings.placeholder               ?? '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialSettings]
  );

  const { values, handleChange, hasChanges } = useChannelForm(defaults);
  const [localError, setLocalError] = useState('');

  const transcriptionEnabled = Boolean(values.input_audio_transcription);
  const transcriptionModel   = values.input_audio_transcription?.model || 'whisper-1';
  const turnDetectionType    = values.turn_detection?.type || '';

  // Ensure the saved model always has an option, even before the catalog loads
  // or if it was later removed from the registry.
  const currentModelValue = values.model || 'gpt-realtime';
  const realtimeOptions = realtimeModels.some((m) => m.id === currentModelValue)
    ? realtimeModels
    : [{ id: currentModelValue, label: currentModelValue }, ...realtimeModels];

  const handleTranscriptionToggle = (enabled) => {
    handleChange('input_audio_transcription', enabled ? { model: transcriptionModel } : null);
  };

  const handleTranscriptionModel = (model) => {
    handleChange('input_audio_transcription', { model });
  };

  const handleTurnDetectionType = (type) => {
    handleChange('turn_detection', type ? { type } : null);
  };

  const handleSaveClick = () => {
    setLocalError('');
    onSave('REALTIME', { ...values });
  };

  return (
    <Box className="assistantSettingsRoot" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {localError && <Alert severity="error">{localError}</Alert>}
      {error      && <Alert severity="error">{error}</Alert>}
      {success    && <Alert severity="success">{success}</Alert>}

      <Box className="assistantSettingsPanel" sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="h6">Realtime Voice</Typography>

        {/* Model selector */}
        <FormControl fullWidth>
          <InputLabel>Model</InputLabel>
          <Select
            value={currentModelValue}
            label="Model"
            onChange={(e) => handleChange('model', e.target.value)}
          >
            {realtimeOptions.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <span>{m.label}</span>
                  {!isMobile && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      {formatPrice(m)}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Voice selector */}
        <FormControl fullWidth>
          <InputLabel>Voice</InputLabel>
          <Select
            value={values.voice || 'alloy'}
            label="Voice"
            onChange={(e) => handleChange('voice', e.target.value)}
          >
            {REALTIME_VOICES.map((v) => (
              <MenuItem key={v.value} value={v.value}>
                {v.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Initial Greeting"
          value={values.placeholder || ''}
          onChange={(e) => handleChange('placeholder', e.target.value)}
          helperText={isMobile ? undefined : 'Spoken by the assistant at the start of every voice call. Leave blank to let the model improvise.'}
          fullWidth
        />

        {/* Instructions */}
        <TextField
          label="Instructions"
          value={values.instructions || ''}
          onChange={(e) => handleChange('instructions', e.target.value)}
          multiline
          minRows={isMobile ? 4 : 6}
          fullWidth
          InputProps={{
            endAdornment: agentId ? (
              <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
                <InstructionHistoryButton
                  agentId={agentId}
                  channelType={channelType}
                  onRestore={(value) => handleChange('instructions', value)}
                />
              </Box>
            ) : null,
          }}
        />

        {/* Turn detection type selector */}
        <FormControl fullWidth>
          <InputLabel>Turn Detection</InputLabel>
          <Select
            value={turnDetectionType}
            label="Turn Detection"
            onChange={(e) => handleTurnDetectionType(e.target.value)}
          >
            {TURN_DETECTION_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Transcription toggle + model selector */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={transcriptionEnabled}
                onChange={(e) => handleTranscriptionToggle(e.target.checked)}
              />
            }
            label="Enable Transcription"
          />
          {transcriptionEnabled && (
            <FormControl fullWidth>
              <InputLabel>Transcription Model</InputLabel>
              <Select
                value={transcriptionModel}
                label="Transcription Model"
                onChange={(e) => handleTranscriptionModel(e.target.value)}
              >
                {TRANSCRIPTION_MODELS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Parallel tool calls */}
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(values.parallel_tool_calls)}
              onChange={(e) => handleChange('parallel_tool_calls', e.target.checked)}
            />
          }
          label="Enable parallel tool calls"
        />

        <Button
          variant="contained"
          onClick={handleSaveClick}
          disabled={!hasChanges || saving}
          className="assistantPrimaryButton assistantButtonAlignEnd"
        >
          {saving ? 'Saving...' : 'Save Realtime Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default RealtimeChannelSettings;
